import React from 'react';

import clsx from 'clsx';

import { NeuIcon, NeuIconProps } from '@hcahealthcaredpx/neutron-react';
import { HighlightText } from '@xsf/shared-lib/components/highlight-text';
import { ModuleLink } from '@xsf/shared-lib/components/module-link';

import { TypeaheadItem } from '../typeahead-new.types';

interface TypeaheadResultsProps {
  query: string;
  typeaheadItems: TypeaheadItem[];
  selectedItem: TypeaheadItem | null;
  resultsCTA?: {
    label: string;
    link: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
  };
  selectItemIcon?: string;
  onSelect: (item: TypeaheadItem) => void;
  onCtaClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  closeDropdown: () => void;
  focusFirstItemRef?: React.MutableRefObject<(() => void) | null>;
}

export const TypeaheadResults: React.FC<TypeaheadResultsProps> = ({
  query,
  typeaheadItems,
  resultsCTA,
  selectedItem,
  selectItemIcon,
  onSelect,
  onCtaClick,
  closeDropdown,
  focusFirstItemRef,
}) => {
  const itemStyles = clsx(
    'neu-text-caption neu-bg-plain-0 neu-bg-gray-10-hover cursor-pointer py-1 transition-background-color duration-300'
  );
  const headerStyles = clsx('neu-text-support', 'neu-bg-gray-0', 'py-0');

  // Track refs for keyboard navigation
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

  // Get total count of selectable items only
  const selectableItemsCount = React.useMemo(
    () => typeaheadItems.filter((item) => item.type === 'item').length,
    [typeaheadItems]
  );

  // Expose function to focus first item from parent
  React.useEffect(() => {
    if (focusFirstItemRef) {
      focusFirstItemRef.current = () => {
        setFocusedIndex(0);
        buttonRefs.current[0]?.focus();
      };
    }
  }, [focusFirstItemRef]);

  // Focus the button when focused index changes
  React.useEffect(() => {
    if (focusedIndex !== null && focusedIndex >= 0) {
      buttonRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  // Handle keyboard navigation within dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = (prev === null ? 0 : prev + 1) % selectableItemsCount;
        return next;
      });
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next =
          prev === null
            ? selectableItemsCount - 1
            : prev <= 0
              ? selectableItemsCount - 1
              : prev - 1;
        return next;
      });
    }
    if (e.key === 'Enter' && focusedIndex !== null) {
      e.preventDefault();
      const selectableItems = typeaheadItems.filter((item) => item.type === 'item');
      if (selectableItems[focusedIndex]) {
        onSelect(selectableItems[focusedIndex]);
      }
    }
  };

  // Track selectable item index separately from all items
  let selectableItemIndex = -1;

  return (
    <div onKeyDown={handleKeyDown}>
      {typeaheadItems.map((item) => {
        const isHeader = item.type === 'header';

        // Only increment for selectable items
        if (!isHeader) {
          selectableItemIndex++;
        }

        const currentSelectableIndex = selectableItemIndex;
        const valueToDisplay = isHeader ? item.value : item.displayedValue || item.value;

        // For headers, use div; for items, use button
        if (isHeader) {
          return (
            <div
              key={item.id}
              role="presentation"
              className={`d-flex align-items-center px-2 ${headerStyles}`}
            >
              <div>
                <HighlightText text={valueToDisplay} highlight={query} />
              </div>
            </div>
          );
        }

        return (
          <button
            type="button"
            id={`${name}-typeahead-option-${item.id}`}
            key={item.id}
            role="option"
            aria-selected={selectedItem?.id === item.id}
            ref={(el) => {
              buttonRefs.current[currentSelectableIndex] = el;
            }}
            className={`d-flex align-items-center px-2 border-0 w-100 text-start ${itemStyles}`}
            onClick={() => {
              onSelect(item);
            }}
            onMouseDown={() => {
              // Ensure the value is set before blur fires
              onSelect(item);
            }}
          >
            <div>
              {selectItemIcon && (
                <NeuIcon name={selectItemIcon as NeuIconProps['name']} className="pe-1" />
              )}
              <HighlightText text={valueToDisplay} highlight={query} />
            </div>
          </button>
        );
      })}
      {typeaheadItems.length > 0 && resultsCTA && (
        <div className="w-100 px-2 py-1 border-top border-neu-gray-10">
          <ModuleLink
            className="neu-text-caption p-0 neu-link--forced"
            href={resultsCTA.link.href}
            target={resultsCTA.link.target}
            rel={resultsCTA.link.rel}
            role="option"
            id={`${name}-typeahead-option-cta`}
            aria-selected={false}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (onCtaClick) {
                onCtaClick(e);
                closeDropdown();
              }
            }}
          >
            {resultsCTA.label}
            <NeuIcon name="arrow_right_alt" className="ps-1" />
          </ModuleLink>
        </div>
      )}
    </div>
  );
};
